import React, { useState, useEffect } from 'react';
import { TrendingUp, Shield, PiggyBank, Target, ArrowRight, Umbrella } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle contact actions based on device type
  const handleContactAction = (type) => {
    if (isMobile) {
      // On mobile, use native apps
      if (type === 'phone') {
        window.location.href = 'tel:+919904449922';
      }
    } else {
      // On desktop, scroll to contact form
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const mainServices = [
    {
      icon: <TrendingUp size={40} />,
      title: "Mutual Fund Investing",
      description: "Pool your investments professionally, diversify effortlessly, and leave the portfolio management to us—so you can focus on your life goals.",
      features: [
        "Professional fund selection",
        "Risk-adjusted returns",
        "SIP & lump sum options",
        "Regular portfolio review"
      ],
      link: "/services/mutual-funds"
    },
    {
      icon: <PiggyBank size={40} />,
      title: "Fixed Deposits & Secure Savings",
      description: "Prefer stability? Company fixed deposits give you assured returns, flexible tenures, and peace of mind with every deposit.",
      features: [
        "Higher interest rates",
        "Flexible tenure options",
        "Capital protection guarantee",
        "Easy liquidity options"
      ],
      link: "/services/fixed-deposits"
    },
    {
      icon: <Target size={40} />,
      title: "Financial Planning & Wealth Guidance",
      description: "Plan for your tomorrow, today. Whether you're building wealth or securing your legacy—our strategic advisement keeps your goals front and center.",
      features: [
        "Goal-based planning",
        "Retirement planning",
        "Tax optimization",
        "Estate planning guidance"
      ],
      link: "/services/financial-planning"
    },
    {
      icon: <Umbrella size={40} />,
      title: "Insurance Services",
      description: "Comprehensive life, health, and child insurance solutions tailored to your needs and financial goals.",
      features: [
        "Life insurance plans",
        "Health insurance coverage",
        "Child insurance solutions",
        "Tax optimization benefits"
      ],
      link: "/services/insurance"
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="heading-section fade-in-up">Our Financial Services</h2>
          <p className="text-large max-w-3xl mx-auto fade-in-up delay-1">
            Comprehensive financial solutions designed to help you achieve your goals with confidence and peace of mind.
          </p>
        </div>

        {/* Main Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 ">
          {mainServices.map((service, index) => (
            <div 
              key={index} 
              className={`service-card fade-in-up delay-${index + 1}`}
            >
              {/* Service Icon */}
              <div className="text-primary-gold mb-6">
                {service.icon}
              </div>

              {/* Service Content */}
              <h3 className="heading-card mb-4">
                {service.title}
              </h3>
              
              <p className="text-body mb-6">
                {service.description}
              </p>

              {/* Service Features */}
              <ul className="space-y-3 mb-6">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-gold rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-body text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Link 
                to={service.link}
                className="text-primary-green font-medium hover:text-primary-gold transition-colors duration-300 flex items-center gap-2 group"
              >
                Learn More
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          ))}
        </div>

        {/* Call to Action Section */}
        <div className="bg-gradient-to-r from-primary-green to-accent-green rounded-3xl pt-6 pb-8 px-8 md:pt-8 md:pb-12 md:px-12 text-center text-white fade-in-up delay-3">
          <h3 className="text-3xl font-poppins font-semibold ">
            Ready to Start Your Financial Journey?
          </h3>
          <p className="text-lg  opacity-90 max-w-2xl mx-auto">
            Let's discuss your financial goals and create a personalized strategy that works for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToContact} className="btn-primary bg-white text-primary-green hover:bg-gray-100">
              <Target size={20} />
              Schedule Consultation
              <ArrowRight size={20} />
            </button>
            <button 
              onClick={() => handleContactAction('phone')}
              className="btn-secondary border-white text-white hover:bg-white hover:text-primary-green"
            >
              {isMobile ? 'Call Now' : 'Contact Us'}
            </button>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-16 text-center fade-in-up delay-4">
          <h3 className="heading-card mb-4">Why Choose NSA Financial Services?</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-light-gold rounded-2xl p-3">
              <div className="text-3xl mb-4">🏆</div>
              <h4 className="font-poppins font-semibold text-primary-green mb-3">
                Proven Track Record
              </h4>
              <p className="text-body text-sm">
                Years of successful client relationships and consistent portfolio performance.
              </p>
            </div>
            <div className="bg-cream-bg rounded-2xl p-6">
              <div className="text-3xl mb-4">🤝</div>
              <h4 className="font-poppins font-semibold text-primary-green mb-3">
                Personal Touch
              </h4>
              <p className="text-body text-sm">
                Every client receives personalized attention and customized financial solutions.
              </p>
            </div>
            <div className="bg-light-gold rounded-2xl p-6">
              <div className="text-3xl mb-4">📞</div>
              <h4 className="font-poppins font-semibold text-primary-green mb-3">
                Always Available
              </h4>
              <p className="text-body text-sm">
                Dedicated support whenever you need guidance or have questions about your investments.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;